import { collection, doc, getDoc, setDoc, getDocs, deleteDoc, query, where, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from './firebase';
import { CategoryCustomizationRules, CustomizationSection } from '../models/Category';
import productService from './productService';
import { adminScopeService } from './adminScopeService';

// Collection Firestore
const CUSTOMIZATION_RULES_COLLECTION = 'customizationRules';

// Type pour un identifiant de catégorie avec flag d'héritage
export interface ProductCustomizationConfig {
  productId: string;
  usesCategoryRules: boolean;
}

 // Conversion des données Firestore en modèle CategoryCustomizationRules
const convertFirestoreDataToRules = (id: string, data: any): CategoryCustomizationRules => {
  return {
    category: data.categoryId ?? id,
    storeId: data.storeId,
    sections: Array.isArray(data.sections) ? data.sections : [],
  };
};

const buildScopedRulesDocId = (storeId: string, categoryId: string): string =>
  `${storeId}__${categoryId}`;

const getRequiredCurrentStoreId = (action: string): string => {
  const currentStoreId = adminScopeService.getCurrentStoreId();
  if (!currentStoreId) {
    throw new Error(`Aucun magasin actif sélectionné pour ${action}.`);
  }
  return currentStoreId;
};

const getScopedRulesDocId = async (
  storeId: string,
  categoryId: string
): Promise<string | null> => {
  const baseRef = collection(db, CUSTOMIZATION_RULES_COLLECTION);
  const scopedSnapshot = await getDocs(
    query(
      baseRef,
      where('storeId', '==', storeId),
      where('categoryId', '==', categoryId)
    )
  );

  if (!scopedSnapshot.empty) {
    return scopedSnapshot.docs[0].id;
  }

  const predictedDocId = buildScopedRulesDocId(storeId, categoryId);
  const predictedDoc = await getDoc(doc(db, CUSTOMIZATION_RULES_COLLECTION, predictedDocId));
  return predictedDoc.exists() ? predictedDoc.id : null;
};

const getRulesReadSnapshot = async (categoryId: string) => {
  const currentStoreId = getRequiredCurrentStoreId(
    'la lecture des règles de personnalisation'
  );
  const scopedDocId = await getScopedRulesDocId(currentStoreId, categoryId);
  if (scopedDocId) {
    return getDoc(doc(db, CUSTOMIZATION_RULES_COLLECTION, scopedDocId));
  }
  return null;
};

const resolveRulesWriteDocId = async (categoryId: string): Promise<string> => {
  const currentStoreId = getRequiredCurrentStoreId(
    'la sauvegarde des règles de personnalisation'
  );
  const scopedDocId = await getScopedRulesDocId(currentStoreId, categoryId);
  return scopedDocId ?? buildScopedRulesDocId(currentStoreId, categoryId);
};

const resolveRulesDeleteDocId = async (categoryId: string): Promise<string | null> => {
  const currentStoreId = getRequiredCurrentStoreId(
    'la suppression des règles de personnalisation'
  );
  return getScopedRulesDocId(currentStoreId, categoryId);
};

// Service pour la gestion des règles de personnalisation
const customizationRulesService = {
  // Récupérer les règles de personnalisation pour une catégorie
  getRulesForCategory: async (category: string): Promise<CategoryCustomizationRules | null> => {
    try {
      const docSnap = await getRulesReadSnapshot(category);
      
      if (!docSnap || !docSnap.exists()) {
        return null;
      }
      
      return convertFirestoreDataToRules(docSnap.id, docSnap.data());
    } catch (error) {
      console.error(`Erreur lors de la récupération des règles pour la catégorie ${category}:`, error);
      throw error;
    }
  },
  
  // Récupérer toutes les règles de personnalisation
  getAllRules: async (): Promise<CategoryCustomizationRules[]> => {
    try {
      const currentStoreId = getRequiredCurrentStoreId(
        'la récupération de toutes les règles de personnalisation'
      );
      const baseRef = collection(db, CUSTOMIZATION_RULES_COLLECTION);
      const querySnapshot = await getDocs(
        query(baseRef, where('storeId', '==', currentStoreId))
      );
      
      return querySnapshot.docs.map(doc => 
        convertFirestoreDataToRules(doc.id, doc.data())
      );
    } catch (error) {
      console.error('Erreur lors de la récupération de toutes les règles:', error);
      throw error;
    }
  },
  
  // Créer ou mettre à jour des règles de personnalisation pour une catégorie
  saveRulesForCategory: async (rules: CategoryCustomizationRules): Promise<void> => {
    try {
      const storeId = getRequiredCurrentStoreId(
        'la sauvegarde des règles de personnalisation'
      );
      const docId = await resolveRulesWriteDocId(rules.category);
      const docRef = doc(db, CUSTOMIZATION_RULES_COLLECTION, docId);
      await setDoc(
        docRef,
        {
          storeId,
          categoryId: rules.category,
          sections: rules.sections,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );
    } catch (error) {
      console.error(`Erreur lors de la sauvegarde des règles pour la catégorie ${rules.category}:`, error);
      throw error;
    }
  },
  
  // Supprimer les règles de personnalisation pour une catégorie
  deleteRulesForCategory: async (category: string): Promise<void> => {
    try {
      const docId = await resolveRulesDeleteDocId(category);
      if (!docId) {
        return;
      }
      await deleteDoc(doc(db, CUSTOMIZATION_RULES_COLLECTION, docId));
    } catch (error) {
      console.error(`Erreur lors de la suppression des règles pour la catégorie ${category}:`, error);
      throw error;
    }
  },
  
  // Appliquer les règles de catégorie à tous les produits de cette catégorie
  applyRulesToAllCategoryProducts: async (
    category: string,
    onlyToProductsUsingCategoryRules: boolean = true
  ): Promise<void> => {
    try {
      // Récupérer les règles de la catégorie (ou considérer sections vides si inexistantes)
      const fetched = await customizationRulesService.getRulesForCategory(category);
      const rules = fetched || { category, sections: [] };

      // Récupérer les produits de cette catégorie
      const products = await productService.getProducts({ category });
      const filtered = onlyToProductsUsingCategoryRules
        ? products.filter(p => p.usesCategoryRules)
        : products;

      if (filtered.length === 0) {
        return;
      }

      // Propagation atomique via batch
      const batch = writeBatch(db);
      filtered.forEach(product => {
        const productRef = doc(db, 'products', product.id);
        batch.update(productRef, {
          customizationSections: rules.sections,
          updatedAt: serverTimestamp()
        });
      });
      await batch.commit();
    } catch (error) {
      console.error(`Erreur lors de l'application des règles aux produits de la catégorie ${category}:`, error);
      throw error;
    }
  },
  
  // Configurer si un produit utilise les règles de sa catégorie
  setProductUseCategoryRules: async (
    productId: string, 
    usesCategoryRules: boolean
  ): Promise<void> => {
    try {
      // 1. Mettre à jour le drapeau dans le produit
      await productService.updateProduct(productId, { usesCategoryRules });
      
      // 2. Si activé, appliquer immédiatement les règles de la catégorie
      if (usesCategoryRules) {
        const product = await productService.getProductById(productId);
        if (product) {
          const categoryRules = await customizationRulesService.getRulesForCategory(product.category);
          if (categoryRules) {
            await productService.updateProduct(productId, {
              customizationSections: categoryRules.sections
            });
          }
        }
      }
    } catch (error) {
      console.error(`Erreur lors de la configuration de l'héritage des règles pour le produit ${productId}:`, error);
      throw error;
    }
  },
  
  // Extraire les règles communes des produits d'une catégorie
  extractCommonRulesFromCategory: async (category: string): Promise<CustomizationSection[] | null> => {
    try {
      // 1. Récupérer les produits de la catégorie
      const products = await productService.getProducts({ category });
      
      // S'il n'y a pas de produits, retourner null
      if (products.length === 0) return null;
      
      // 2. Trouver les produits qui ont des règles de personnalisation
      const productsWithRules = products.filter(
        p => p.customizationSections && p.customizationSections.length > 0
      );
      
      // S'il n'y a pas de produits avec règles, retourner null
      if (productsWithRules.length === 0) return null;
      
      // 3. Utiliser le premier produit comme référence
      const referenceProduct = productsWithRules[0];
      const referenceSections = referenceProduct.customizationSections || [];
      
      // 4. Vérifier si tous les autres produits ont les mêmes sections
      const allHaveSameSections = productsWithRules.every(product => {
        const productSections = product.customizationSections || [];
        
        // Vérifier si le nombre de sections est le même
        if (productSections.length !== referenceSections.length) return false;
        
        // Vérifier que chaque section correspond
        return referenceSections.every((refSection, index) => {
          const productSection = productSections[index];
          
          // Vérifier les propriétés principales
          return (
            productSection.type === refSection.type &&
            productSection.title === refSection.title
          );
        });
      });
      
      // 5. Si tous les produits ont les mêmes sections, retourner ces sections
      if (allHaveSameSections) {
        return referenceSections;
      }
      
      return null;
    } catch (error) {
      console.error(`Erreur lors de l'extraction des règles communes pour la catégorie ${category}:`, error);
      throw error;
    }
  },
  
  // Utilitaire de migration pour extraire les règles et configurer l'héritage
  migrateToInheritance: async (category: string): Promise<boolean> => {
    try {
      // 1. Essayer d'extraire les règles communes
      const commonRules = await customizationRulesService.extractCommonRulesFromCategory(category);
      
      // Si pas de règles communes, on ne peut pas migrer
      if (!commonRules) return false;
      
      // 2. Enregistrer les règles communes au niveau de la catégorie
      await customizationRulesService.saveRulesForCategory({
        category,
        sections: commonRules
      });
      
      // 3. Configurer tous les produits pour utiliser les règles de catégorie
      const products = await productService.getProducts({ category });
      
      for (const product of products) {
        await customizationRulesService.setProductUseCategoryRules(product.id, true);
      }
      
      return true;
    } catch (error) {
      console.error(`Erreur lors de la migration vers l'héritage pour la catégorie ${category}:`, error);
      return false;
    }
  }
};

export default customizationRulesService;
